import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  // InputAdornment,  ← restore with the surcharge field below (~line 681)
  MenuItem,
  Popover,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close, FormatListNumbered, InfoOutlined } from '@mui/icons-material';
import { colors } from '../../theme/theme';
import SeatMapCanvas, { slotStyles } from './SeatMapCanvas';
import {
  LAYOUT_LIMITS,
  RoomOrientation,
  SeatLayout,
  SeatRow,
  SeatPosition,
  SEAT_CYCLE,
  bookableCount,
  makeSeat,
  maxRowLength,
  nextLabelNumber,
  oosCount,
  physicalCount,
  renumberAll,
  rowLabelForIndex,
  newId,
  surchargedRows,
  validateLayout,
} from '../../types/seatLayout';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  SEAT LAYOUT EDITOR
 * ─────────────────────────────────────────────────────────────────────────────
 * Owns all room mutation. The canvas is pure presentation; every edit lands here
 * so the invariants live in one place:
 *
 *  • TWO NUMBERING MODES, see `SeatLayout.autoNumber`.
 *      AUTO (default): every structural edit re-flows numbers 1…N positionally,
 *        so removing a bike shifts the rest down and the room never shows holes
 *        like "9, 10, 11, 32, 33".
 *      MANUAL: numbers are left exactly as typed, for a room whose physical
 *        decals are already fixed — a decal does not move when you insert a
 *        walkway. Editing any number by hand switches the room into this mode.
 *  • Every structural edit goes through `commit`, which is what applies the
 *    numbering pass. A mutation that bypassed it would desync the room.
 *  • In MANUAL mode, cycling a bike to a walkway PRESERVES its number so cycling
 *    back is lossless. In AUTO mode the re-flow makes that moot.
 *  • Adding a slot takes `max(existing number) + 1`, never `count + 1`, so in
 *    MANUAL mode a new bike cannot collide with an existing decal.
 *  • Row labels ARE positional and do relabel on add/delete. That is safe: rows
 *    are not booked, bikes are. Surcharge travels with the row object, so a
 *    deleted row never leaks its surcharge onto its replacement.
 */

type Draft = Omit<SeatLayout, '_id' | 'createdAt' | 'updatedAt'> &
  Partial<Pick<SeatLayout, '_id'>>;

interface SeatLayoutEditorProps {
  open: boolean;
  initial: Draft;
  saving?: boolean;
  onClose: () => void;
  onSave: (draft: Draft) => void;
}

const ORIENTATIONS: { value: RoomOrientation; label: string }[] = [
  { value: 'instructor_top', label: 'Instructor at the top' },
  { value: 'instructor_bottom', label: 'Instructor at the bottom' },
  { value: 'instructor_left', label: 'Instructor on the left' },
  { value: 'instructor_right', label: 'Instructor on the right' },
];

const SeatLayoutEditor: React.FC<SeatLayoutEditorProps> = ({
  open,
  initial,
  saving = false,
  onClose,
  onSave,
}) => {
  /**
   * Rooms saved before `autoNumber` existed have it undefined, which would read
   * as falsy and silently put them in MANUAL mode. Default to AUTO on the way in.
   */
  const withDefaults = (d: Draft): Draft => ({ ...d, autoNumber: d.autoNumber ?? true });

  const [draft, setDraft] = useState<Draft>(withDefaults(initial));
  const [renumberAsk, setRenumberAsk] = useState(false);

  // Popover targets
  const [slotEdit, setSlotEdit] = useState<{
    rowId: string;
    slot: SeatPosition;
    anchor: HTMLElement;
  } | null>(null);
  const [rowEdit, setRowEdit] = useState<{
    row: SeatRow;
    anchor: HTMLElement;
  } | null>(null);

  // Re-seed when the dialog is reopened for a different room.
  const [seededFor, setSeededFor] = useState<string | undefined>(initial._id);
  if (open && seededFor !== initial._id) {
    setDraft(withDefaults(initial));
    setSeededFor(initial._id);
  }

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  /**
   * The single funnel for every STRUCTURAL edit (add/remove slot, insert
   * walkway, cycle state, add/delete row).
   *
   * In AUTO mode it renumbers 1…N positionally after the change, so removing a
   * bike shifts everything after it down and the room always reads 1…N with no
   * holes. Routing every mutation through here is what stops numbering drifting
   * out of sync — an edit path that forgot to renumber is exactly how a row ends
   * up reading "9, 10, 11, 32, 33".
   *
   * In MANUAL mode it leaves numbers untouched.
   */
  const commit = useCallback(
    (fn: (d: Draft) => Draft) =>
      setDraft((d) => {
        const next = fn(d);
        return next.autoNumber === false
          ? next
          : { ...next, rows: renumberAll(next.rows) };
      }),
    [],
  );

  /** Non-structural row/slot field edits — never trigger renumbering. */
  const setRows = useCallback(
    (fn: (rows: SeatRow[]) => SeatRow[]) =>
      setDraft((d) => ({ ...d, rows: fn(d.rows) })),
    [],
  );

  /** Keep row labels positional (A, B, C…) after add/delete. */
  const relabelRows = (rows: SeatRow[]): SeatRow[] =>
    rows.map((r, i) => ({ ...r, label: rowLabelForIndex(i) }));

  /* ── Slot mutations ─────────────────────────────────────────────────────── */

  const cycleSlot = useCallback(
    (rowId: string, seatId: string) =>
      commit((d) => {
        const nextNum = nextLabelNumber(d);
        return {
          ...d,
          rows: d.rows.map((row) =>
            row.rowId !== rowId
              ? row
              : {
                  ...row,
                  seats: row.seats.map((s) => {
                    if (s.seatId !== seatId) return s;
                    const next =
                      SEAT_CYCLE[
                        (SEAT_CYCLE.indexOf(s.state) + 1) % SEAT_CYCLE.length
                      ];
                    return {
                      ...s,
                      state: next,
                      // MANUAL mode: a gap keeps its decal so cycling back is
                      // lossless. AUTO mode: `commit` renumbers straight after,
                      // so this value is transient either way.
                      label:
                        next === 'gap' ? s.label : (s.label ?? String(nextNum)),
                      // Leaving OOS clears the maintenance note — it no longer applies.
                      oosReason: next === 'oos' ? s.oosReason : null,
                      oosExpectedBackAt:
                        next === 'oos' ? s.oosExpectedBackAt : null,
                    };
                  }),
                },
          ),
        };
      }),
    [commit],
  );

  const insertGap = useCallback(
    (rowId: string, index: number) =>
      commit((d) => ({
        ...d,
        rows: d.rows.map((row) => {
          if (row.rowId !== rowId) return row;
          if (row.seats.length >= LAYOUT_LIMITS.MAX_SLOTS_PER_ROW) return row;
          const seats = [...row.seats];
          seats.splice(index, 0, makeSeat('gap', null));
          return { ...row, seats };
        }),
      })),
    [commit],
  );

  const changeRowCount = useCallback(
    (rowId: string, delta: 1 | -1) =>
      commit((d) => {
        const nextNum = nextLabelNumber(d);
        return {
          ...d,
          rows: d.rows.map((row) => {
            if (row.rowId !== rowId) return row;
            if (delta === 1) {
              if (row.seats.length >= LAYOUT_LIMITS.MAX_SLOTS_PER_ROW) return row;
              return {
                ...row,
                seats: [...row.seats, makeSeat('bookable', String(nextNum))],
              };
            }
            if (row.seats.length <= LAYOUT_LIMITS.MIN_SLOTS_PER_ROW) return row;
            return { ...row, seats: row.seats.slice(0, -1) };
          }),
        };
      }),
    [commit],
  );

  const deleteRow = useCallback(
    (rowId: string) =>
      commit((d) => ({
        ...d,
        rows: relabelRows(d.rows.filter((r) => r.rowId !== rowId)),
      })),
    [commit],
  );

  /** New rows match the last row's length — studios build symmetric rooms. */
  const addRow = useCallback(
    () =>
      commit((d) => {
        if (d.rows.length >= LAYOUT_LIMITS.MAX_ROWS) return d;
        const width = d.rows.length ? d.rows[d.rows.length - 1].seats.length : 8;
        let n = nextLabelNumber(d);
        const row: SeatRow = {
          rowId: newId('row'),
          label: rowLabelForIndex(d.rows.length),
          surcharge: 0,
          seats: Array.from({ length: width }, () =>
            makeSeat('bookable', String(n++)),
          ),
        };
        return { ...d, rows: relabelRows([...d.rows, row]) };
      }),
    [commit],
  );

  const patchSlot = (rowId: string, seatId: string, patch: Partial<SeatPosition>) =>
    setRows((rows) =>
      rows.map((row) =>
        row.rowId !== rowId
          ? row
          : {
              ...row,
              seats: row.seats.map((s) =>
                s.seatId === seatId ? { ...s, ...patch } : s,
              ),
            },
      ),
    );

  const patchRow = (rowId: string, patch: Partial<SeatRow>) =>
    setRows((rows) =>
      rows.map((r) => (r.rowId === rowId ? { ...r, ...patch } : r)),
    );

  /* ── Derived ────────────────────────────────────────────────────────────── */

  const stats = useMemo(
    () => ({
      bookable: bookableCount(draft),
      oos: oosCount(draft),
      physical: physicalCount(draft),
      rows: draft.rows.length,
      widest: maxRowLength(draft),
      surcharged: surchargedRows(draft),
    }),
    [draft],
  );

  const issues = useMemo(() => validateLayout(draft), [draft]);
  const canSave = issues.length === 0 && !saving;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">
              {initial._id ? 'Edit room' : 'New room'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Click a bike to cycle it: bookable → out of service → walkway. Use
              the thin strips between bikes to push a walkway in, and −/+ to
              change a row's length. Right-click a bike for its number and
              options.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Room settings */}
        <Stack spacing={2} mb={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Room name *"
              size="small"
              fullWidth
              value={draft.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Spin Studio A, Reformer Room"
            />
            <TextField
              label="Orientation"
              size="small"
              select
              fullWidth
              value={draft.orientation}
              onChange={(e) =>
                set('orientation', e.target.value as RoomOrientation)
              }
              helperText="Which wall the instructor is on. Row A is always the row nearest them."
            >
              {ORIENTATIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Front caption"
              size="small"
              fullWidth
              value={draft.frontLabel}
              onChange={(e) => set('frontLabel', e.target.value)}
              placeholder="INSTRUCTOR"
            />
            <TextField
              label="Back caption"
              size="small"
              fullWidth
              value={draft.backLabel}
              onChange={(e) => set('backLabel', e.target.value)}
              placeholder="ENTRANCE"
            />
            <TextField
              label="Description"
              size="small"
              fullWidth
              value={draft.description ?? ''}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Optional note for staff"
            />
          </Stack>

          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            {/* No "Active" toggle: a room is in use if a class points at it, and
                retiring one is Delete (a soft delete the server refuses while
                upcoming sessions still need the room). */}

            {/* Numbering mode. Auto keeps the room reading 1…N as you edit;
                manual is for rooms whose physical decals are already fixed. */}
            <Chip
              size="small"
              icon={<FormatListNumbered sx={{ fontSize: 16 }} />}
              label={
                draft.autoNumber
                  ? 'Auto-numbered 1–' + stats.physical
                  : 'Manual numbers'
              }
              color={draft.autoNumber ? 'primary' : 'default'}
              variant={draft.autoNumber ? 'filled' : 'outlined'}
            />
            {!draft.autoNumber && (
              <Tooltip title="Reassign every number in positional order and go back to auto-numbering.">
                <Button
                  size="small"
                  startIcon={<FormatListNumbered />}
                  onClick={() => setRenumberAsk(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Renumber &amp; go back to auto
                </Button>
              </Tooltip>
            )}
          </Box>
        </Stack>

        {issues.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Stack spacing={0.25}>
              {issues.map((i, idx) => (
                <Typography key={idx} variant="body2">
                  {i.message}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        {/* The room */}
        <SeatMapCanvas
          rows={draft.rows}
          frontLabel={draft.frontLabel}
          backLabel={draft.backLabel}
          orientation={draft.orientation}
          onCycleSlot={cycleSlot}
          onInsertGap={insertGap}
          onRowCountChange={changeRowCount}
          onDeleteRow={deleteRow}
          onAddRow={addRow}
          onOpenSlot={(rowId, slot, anchor) =>
            setSlotEdit({ rowId, slot, anchor })
          }
          onOpenRow={(row, anchor) => setRowEdit({ row, anchor })}
        />

        {/* Legend + stats */}
        <Box
          mt={2}
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          gap={2}
          sx={{
            p: 1.5,
            borderRadius: 2,
            background: colors.backgroundTertiary,
            border: `1px solid ${colors.divider}`,
          }}
        >
          <LegendKey label="Bookable" style={slotStyles('bookable', false)} />
          {/* Per-row surcharge chips — hidden. Nothing charges a surcharge, so
              advertising "Row A · +₹100" to staff described a price that does
              not exist. Restore when row pricing is wired end to end:
          {stats.surcharged.map((r) => (
            <LegendKey
              key={r.rowId}
              label={`Row ${r.label} · +₹${r.surcharge}`}
              style={slotStyles('bookable', true)}
            />
          ))} */}
          <LegendKey label="Out of service" style={slotStyles('oos', false)} />
          <LegendKey label="Walkway" style={slotStyles('gap', false)} />

          <Box flex={1} />

          <Typography variant="caption" color="text.secondary">
            <strong style={{ color: colors.textPrimary }}>
              {stats.bookable}
            </strong>{' '}
            bookable · <strong>{stats.rows}</strong> rows ·{' '}
            <strong>{stats.oos}</strong> out of service
          </Typography>
        </Box>

        <Alert
          severity="info"
          icon={<InfoOutlined fontSize="small" />}
          sx={{ mt: 2 }}
        >
          Class capacity will be derived from the{' '}
          <strong>{stats.bookable} bookable</strong> bikes ({stats.physical}{' '}
          bikes exist, widest row {stats.widest} seats).{' '}
          {draft.autoNumber ? (
            <>
              Numbers run <strong>1–{stats.physical}</strong> across the room and
              re-flow as you add or remove bikes, so there are never gaps in the
              sequence. Type a number by hand to switch to manual.
            </>
          ) : (
            <>
              <strong>Manual numbering</strong> — bikes keep the numbers you set,
              so inserting a walkway never changes anyone's bike. Use "Renumber"
              to go back to automatic 1–{stats.physical}.
            </>
          )}
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(draft)}
          disabled={!canSave}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {initial._id ? 'Save room' : 'Create room'}
        </Button>
      </DialogActions>

      {/* ── Slot popover ───────────────────────────────────────────────────── */}
      <Popover
        open={Boolean(slotEdit)}
        anchorEl={slotEdit?.anchor ?? null}
        onClose={() => setSlotEdit(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {slotEdit && (
          <Box p={2} width={270}>
            <Typography variant="subtitle2" gutterBottom>
              Bike {slotEdit.slot.label ?? '—'}
            </Typography>
            <Stack spacing={1.5} mt={1}>
              <TextField
                label="Number on the bike"
                size="small"
                fullWidth
                value={
                  draft.rows
                    .find((r) => r.rowId === slotEdit.rowId)
                    ?.seats.find((s) => s.seatId === slotEdit.slot.seatId)
                    ?.label ?? ''
                }
                onChange={(e) => {
                  // Typing a number by hand means the studio is matching physical
                  // decals, so drop the room out of auto-numbering — otherwise the
                  // very next edit would overwrite what was just typed.
                  patchSlot(slotEdit.rowId, slotEdit.slot.seatId, {
                    label: e.target.value,
                  });
                  if (draft.autoNumber) set('autoNumber', false);
                }}
                helperText={
                  draft.autoNumber
                    ? 'Editing this switches the room to manual numbering.'
                    : 'Match the decal on the machine.'
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={Boolean(
                      draft.rows
                        .find((r) => r.rowId === slotEdit.rowId)
                        ?.seats.find((s) => s.seatId === slotEdit.slot.seatId)
                        ?.isAccessible,
                    )}
                    onChange={(e) =>
                      patchSlot(slotEdit.rowId, slotEdit.slot.seatId, {
                        isAccessible: e.target.checked,
                      })
                    }
                  />
                }
                label={<Typography variant="body2">Accessible bike</Typography>}
              />

              {slotEdit.slot.state === 'oos' && (
                <>
                  <Divider />
                  <TextField
                    label="Why is it out of service?"
                    size="small"
                    fullWidth
                    value={
                      draft.rows
                        .find((r) => r.rowId === slotEdit.rowId)
                        ?.seats.find((s) => s.seatId === slotEdit.slot.seatId)
                        ?.oosReason ?? ''
                    }
                    onChange={(e) =>
                      patchSlot(slotEdit.rowId, slotEdit.slot.seatId, {
                        oosReason: e.target.value,
                      })
                    }
                    placeholder="e.g. broken pedal"
                  />
                  <TextField
                    label="Expected back"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={
                      draft.rows
                        .find((r) => r.rowId === slotEdit.rowId)
                        ?.seats.find((s) => s.seatId === slotEdit.slot.seatId)
                        ?.oosExpectedBackAt ?? ''
                    }
                    onChange={(e) =>
                      patchSlot(slotEdit.rowId, slotEdit.slot.seatId, {
                        oosExpectedBackAt: e.target.value,
                      })
                    }
                  />
                </>
              )}
            </Stack>
          </Box>
        )}
      </Popover>

      {/* ── Row popover ────────────────────────────────────────────────────── */}
      <Popover
        open={Boolean(rowEdit)}
        anchorEl={rowEdit?.anchor ?? null}
        onClose={() => setRowEdit(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {rowEdit && (
          <Box p={2} width={260}>
            <Typography variant="subtitle2" gutterBottom>
              Row {rowEdit.row.label}
            </Typography>
            <Stack spacing={1.5} mt={1}>
              <TextField
                label="Row label"
                size="small"
                fullWidth
                value={
                  draft.rows.find((r) => r.rowId === rowEdit.row.rowId)?.label ??
                  ''
                }
                onChange={(e) =>
                  patchRow(rowEdit.row.rowId, { label: e.target.value })
                }
              />
              {/* Row surcharge — hidden. It saved a number that no booking path
                  ever reads, so a studio could set "+₹150 for row A" and be
                  charged nothing for it, with no way to tell from the UI.
                  A field that silently does nothing is worse than no field.
                  Existing stored values are untouched (the schema, DTO and API
                  all still carry `surcharge`) — restore this input, the legend
                  chips above and the ring in SeatMapCanvas together, once a
                  booking path actually charges it.
              <TextField
                label="Surcharge for this row"
                size="small"
                type="number"
                fullWidth
                value={
                  draft.rows.find((r) => r.rowId === rowEdit.row.rowId)
                    ?.surcharge ?? 0
                }
                onChange={(e) =>
                  patchRow(rowEdit.row.rowId, {
                    surcharge: Math.max(0, Number(e.target.value) || 0),
                  })
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                }}
                helperText="Stored now, not charged yet — pricing lands with the backend."
              /> */}
            </Stack>
          </Box>
        )}
      </Popover>

      {/* ── Renumber confirm ───────────────────────────────────────────────── */}
      <Dialog open={renumberAsk} onClose={() => setRenumberAsk(false)}>
        <DialogTitle>Renumber every bike?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Numbers will be reassigned 1–{stats.physical} in positional order,
            skipping walkways, and the room goes back to auto-numbering.
            Out-of-service bikes keep a number because the machine still exists.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5 }} color="text.secondary">
            Any numbers you typed by hand will be replaced.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenumberAsk(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setDraft((d) => ({
                ...d,
                autoNumber: true,
                rows: renumberAll(d.rows),
              }));
              setRenumberAsk(false);
            }}
          >
            Renumber
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

const LegendKey: React.FC<{ label: string; style: React.CSSProperties }> = ({
  label,
  style,
}) => (
  <Box display="flex" alignItems="center" gap={0.75}>
    <Box sx={{ width: 14, height: 14, borderRadius: 0.75, ...style }} />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

export default SeatLayoutEditor;
